import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { XCircle, User, FileText, Save, Link as LinkIcon, ClipboardList, Copy, Check, RotateCcw, ChevronDown } from 'lucide-react';
import { MODULES } from '../../config/moduleDefs';
import { SERVICE_EXTERNAL_SECTION_ORDER, checkLocalFolderLauncher, getAvailableExternalLinkSections, getModuleEntity, getServiceExternalSectionBehavior, getServiceScopedLinks, isServiceExternalSectionHidden, isServiceSectionDefaultOpen, pollForLauncher, restartLocalFolderLauncher, tryLaunchLocalFolderPath } from '../../config/moduleHelpers';
import { supabase } from '../../services/supabase';

const ModalAccordionSection = ({
    title,
    icon,
    defaultOpen = false,
    defaultPresetOpen = false,
    forceOpenState = null,
    forceSignal = 0,
    defaultSignal = 0,
    children,
}) => {
    const detailsRef = useRef(null);

    useEffect(() => {
        if (detailsRef.current) {
            detailsRef.current.open = defaultOpen;
        }
    }, [defaultOpen]);

    useEffect(() => {
        if (typeof forceOpenState === 'boolean' && detailsRef.current) {
            detailsRef.current.open = forceOpenState;
        }
    }, [forceOpenState, forceSignal]);

    useEffect(() => {
        if (detailsRef.current) {
            detailsRef.current.open = defaultPresetOpen;
        }
    }, [defaultPresetOpen, defaultSignal]);

    return (
        <details ref={detailsRef} className="rounded-xl border border-slate-200 bg-white group">
            <summary className="list-none cursor-pointer select-none px-4 py-3 flex items-center justify-between gap-3 hover:bg-slate-50 rounded-xl">
                <div className="flex items-center gap-2 text-slate-800 font-semibold">
                    {icon}
                    <h4>{title}</h4>
                </div>
                <ChevronDown className="w-4 h-4 text-slate-400 transition-transform group-open:rotate-180" />
            </summary>
            <div className="px-4 pb-4 pt-1 border-t border-slate-100">
                {children}
            </div>
        </details>
    );
};

const ClientDetailModal = ({
    client, config, onClose, onSave,
    onConnectHmrc, isConnectingHmrc = false,
    onRequestAuth, onCheckInviteStatus, onSandboxAccept, onOpenInviteLink,
    isRequestingAuth = false, isCheckingInvite = false,
    hmrcEnvironment = 'unknown',
}) => {
    const [formData, setFormData] = useState({ ...client });
    const [sourceLinkData, setSourceLinkData] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [postcodeInput, setPostcodeInput] = useState(client?.hmrc_client_postcode || '');
    const [localLaunchNotice, setLocalLaunchNotice] = useState('');
    const [launcherStatus, setLauncherStatus] = useState('unknown');
    const [copiedLocalLinkKey, setCopiedLocalLinkKey] = useState('');
    const [isRestartingLauncher, setIsRestartingLauncher] = useState(false);
    const [accordionCommand, setAccordionCommand] = useState({ open: null, nonce: 0 });
    const [defaultAccordionNonce, setDefaultAccordionNonce] = useState(0);

    useEffect(() => {
        setFormData({ ...client });
        setPostcodeInput(client?.hmrc_client_postcode || '');
        setLocalLaunchNotice('');
    }, [client]);

    useEffect(() => {
        if (!localLaunchNotice) return undefined;

        const timeoutId = window.setTimeout(() => setLocalLaunchNotice(''), 2600);
        return () => window.clearTimeout(timeoutId);
    }, [localLaunchNotice]);

    useEffect(() => {
        if (!copiedLocalLinkKey) return undefined;

        const timeoutId = window.setTimeout(() => setCopiedLocalLinkKey(''), 1400);
        return () => window.clearTimeout(timeoutId);
    }, [copiedLocalLinkKey]);

    const entity = getModuleEntity(config);
    const mainField = config?.fieldMappings?.mainField || 'company_name';
    const numberField = config?.fieldMappings?.numberField || 'company_number';
    const isIndividual = config?.id === 'individual';
    const isMtdItsa = config?.id === 'mtd_itsa';
    const sourceTypeKey = String(formData?.source_type || '').trim().toLowerCase();
    const sourceModuleMap = {
        company: MODULES.COMPANY,
        charity: MODULES.CHARITY,
        individual: MODULES.INDIVIDUAL,
        unregistered: MODULES.UNREGISTERED,
        partnership: MODULES.PARTNERSHIP,
    };
    const sourceConfig = sourceModuleMap[sourceTypeKey] || null;
    const effectiveSectionConfig = sourceConfig || config;
    const externalLinkSections = Array.isArray(effectiveSectionConfig?.externalLinkSections)
        ? effectiveSectionConfig.externalLinkSections
        : (effectiveSectionConfig?.externalLinks ? [effectiveSectionConfig.externalLinks] : []);
    const isServiceModule = Boolean(config?.enrollMode && sourceTypeKey);

    const sourceTableByType = {
        company: 'clients_coy',
        charity: 'clients_charity',
        individual: 'clients_individual',
        unregistered: 'clients_unregistered',
        partnership: 'clients_partnership',
    };

    const linkContextData = (() => {
        if (!sourceLinkData) return formData;

        const merged = { ...sourceLinkData };
        Object.entries(formData || {}).forEach(([key, value]) => {
            const isEmptyString = typeof value === 'string' && value.trim() === '';
            const isNullish = value === null || value === undefined;

            if (isNullish || isEmptyString) return;

            // On service pages, keep source-table service flags authoritative for link visibility.
            if (isServiceModule && key.startsWith('svc_') && sourceLinkData[key] !== undefined) return;

            merged[key] = value;
        });

        return merged;
    })();

    const svcModalConfig = getServiceExternalSectionBehavior(config?.id);

    // Returns true if a named external-link section should be open when the modal first renders
    const isSectionDefaultOpen = (sectionLabel) => {
        if (isServiceModule) return isServiceSectionDefaultOpen(config?.id, sectionLabel);
        return sectionLabel === 'Client Folders'; // fallback for non-service entity pages
    };

    const primaryWorkstream = Array.isArray(config?.workstreams) ? config.workstreams[0] : null;
    const workflowDueField = primaryWorkstream?.dueField || null;
    const workflowPlanField = primaryWorkstream?.planField || null;
    const workflowStatusField = primaryWorkstream?.statusField || null;
    const workflowStaffField = primaryWorkstream?.staffField || null;
    const workflowRefField = primaryWorkstream?.refField || null;
    const workflowNextField = primaryWorkstream?.nextField || null;
    const orderedExternalLinkSections = [...externalLinkSections].sort((left, right) => {
        const leftKey = left?.sectionLabel || left?.providerLabel || '';
        const rightKey = right?.sectionLabel || right?.providerLabel || '';
        const leftOrder = SERVICE_EXTERNAL_SECTION_ORDER[leftKey] ?? 99;
        const rightOrder = SERVICE_EXTERNAL_SECTION_ORDER[rightKey] ?? 99;

        if (leftOrder !== rightOrder) return leftOrder - rightOrder;
        return leftKey.localeCompare(rightKey);
    }).filter((section) => {
        if (!isServiceModule) return true;
        const sectionLabel = section?.sectionLabel || section?.providerLabel || '';
        return !isServiceExternalSectionHidden(sectionLabel);
    });
    const clientFolderSections = orderedExternalLinkSections.filter((section) => (section?.sectionLabel || section?.providerLabel) === 'Client Folders');
    const remainingExternalLinkSections = orderedExternalLinkSections.filter((section) => (section?.sectionLabel || section?.providerLabel) !== 'Client Folders');
    const resolvedExternalSections = getAvailableExternalLinkSections(effectiveSectionConfig, linkContextData);
    const hasLocalFolderLinks = resolvedExternalSections.some((section) =>
        (section?.links || []).some((link) => /^(file:\/\/\/|ms-explorer:)/i.test(String(link?.url || '')))
    );

    useEffect(() => {
        if (!isServiceModule || !sourceTypeKey || !formData?.source_id) {
            setSourceLinkData(null);
            return undefined;
        }

        const sourceTable = sourceTableByType[sourceTypeKey];
        if (!sourceTable) {
            setSourceLinkData(null);
            return undefined;
        }

        let active = true;

        (async () => {
            const { data } = await supabase
                .from(sourceTable)
                .select('*')
                .eq('id', formData.source_id)
                .maybeSingle();

            if (active) {
                setSourceLinkData(data || null);
            }
        })();

        return () => {
            active = false;
        };
    }, [isServiceModule, sourceTypeKey, formData?.source_id]);

    useEffect(() => {
        if (!hasLocalFolderLinks) return undefined;

        let active = true;
        setLauncherStatus('checking');

        checkLocalFolderLauncher().then((isOnline) => {
            if (active) {
                setLauncherStatus(isOnline ? 'online' : 'offline');
            }
        });

        return () => {
            active = false;
        };
    }, [hasLocalFolderLinks]);
    const hmrcAuthStatus = String(formData.hmrc_auth_status || 'not_connected');
    const hmrcAgentStatus = String(formData.hmrc_agent_auth_status || 'unknown');
    const hasClientOrAgentNotAuthorisedError = String(formData.hmrc_last_error || '').includes('CLIENT_OR_AGENT_NOT_AUTHORISED');
    const showClientOrAgentNotAuthorisedWarning = hasClientOrAgentNotAuthorisedError && (formData.hmrc_invitation_status !== 'pending');
    const effectiveHmrcAgentStatus = showClientOrAgentNotAuthorisedWarning ? 'not_authorised' : hmrcAgentStatus;
    const hmrcInviteStatusRaw = formData.hmrc_invitation_status || null;
    const hmrcInviteStatus = (showClientOrAgentNotAuthorisedWarning && hmrcInviteStatusRaw === 'accepted')
        ? 'accepted_conflict'
        : hmrcInviteStatusRaw;
    const hmrcInviteId      = formData.hmrc_invitation_id      || null;
    const canRequestNew     = !hmrcInviteStatus || ['rejected', 'expired', 'cancelled'].includes(hmrcInviteStatus);
    const isInvitePending   = hmrcInviteStatus === 'pending';
    const isInviteAccepted  = hmrcInviteStatus === 'accepted' && !showClientOrAgentNotAuthorisedWarning;

    const hmrcInviteStatusLabelMap = {
        pending:   'Pending — awaiting client acceptance',
        accepted:  'Authorised',
        accepted_simulated: 'Accepted (Sandbox simulated)',
        accepted_conflict: 'Accepted (HMRC sync says not authorised)',
        rejected:  'Rejected by client',
        expired:   'Expired',
        cancelled: 'Cancelled',
    };
    const hmrcInviteStatusColorMap = {
        pending:   'bg-amber-100 text-amber-700 border-amber-200',
        accepted:  'bg-emerald-100 text-emerald-700 border-emerald-200',
        accepted_simulated: 'bg-blue-100 text-blue-700 border-blue-200',
        accepted_conflict: 'bg-amber-100 text-amber-700 border-amber-200',
        rejected:  'bg-red-100 text-red-700 border-red-200',
        expired:   'bg-slate-100 text-slate-600 border-slate-200',
        cancelled: 'bg-slate-100 text-slate-600 border-slate-200',
    };
    const hmrcStatusLabelMap = {
        not_connected: 'Not Connected',
        connected: 'Connected',
        expired: 'Expired',
        revoked: 'Revoked',
        error: 'Error'
    };
    const hmrcStatusColorMap = {
        not_connected: 'bg-slate-100 text-slate-600 border-slate-200',
        connected: 'bg-emerald-100 text-emerald-700 border-emerald-200',
        expired: 'bg-amber-100 text-amber-700 border-amber-200',
        revoked: 'bg-red-100 text-red-700 border-red-200',
        error: 'bg-red-100 text-red-700 border-red-200'
    };
    const hmrcAgentStatusLabelMap = {
        unknown: 'Unknown',
        authorised: 'Authorised',
        not_authorised: 'Not Authorised',
        error: 'Error'
    };
    const hmrcAgentStatusColorMap = {
        unknown: 'bg-slate-100 text-slate-600 border-slate-200',
        authorised: 'bg-emerald-100 text-emerald-700 border-emerald-200',
        not_authorised: 'bg-amber-100 text-amber-700 border-amber-200',
        error: 'bg-red-100 text-red-700 border-red-200'
    };

    const formatCurrency = (value) => {
        if (value === null || value === undefined || value === '') return 'N/A'
        const number = Number(value)
        return Number.isNaN(number) ? 'N/A' : `£${number.toFixed(2)}`
    }

    const formatDateValue = (value) => {
        if (!value) return 'N/A'
        const date = new Date(String(value))
        return Number.isNaN(date.getTime()) ? 'N/A' : date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })
    }

    const formatSourceTypeLabel = (value) => {
        const normalized = String(value || '').trim().toLowerCase();
        if (!normalized) return 'N/A';

        const labels = {
            company: 'Company',
            charity: 'Charity',
            individual: 'Individual',
            unregistered: 'Unregistered',
            partnership: 'Partnership',
        };

        return labels[normalized] || normalized.charAt(0).toUpperCase() + normalized.slice(1);
    }

    const displayValue = (value) => {
        if (value === null || value === undefined || value === '') return 'N/A'
        return String(value)
    }

    const getExternalReferenceValue = (section) => {
        if (!section?.referenceField) return ''
        return String(linkContextData[section.referenceField] || '').trim()
    }

    const parseJsonMaybe = (value) => {
        if (value == null || value === '') return null
        if (typeof value === 'string') {
            try {
                return JSON.parse(value)
            } catch {
                return null
            }
        }
        return value
    }

    const toArray = (value) => {
        if (Array.isArray(value)) return value
        const parsed = parseJsonMaybe(value)
        if (Array.isArray(parsed)) return parsed
        if (parsed && typeof parsed === 'object') {
            if (Array.isArray(parsed.businesses)) return parsed.businesses
            if (Array.isArray(parsed.items)) return parsed.items
            return [parsed]
        }
        return []
    }

    const mtdIncomeTaxId =
        formData.hmrc_mtd_income_tax_id ||
        formData.hmrc_mtditid ||
        formData.mtd_income_tax_id ||
        formData.mtditid ||
        null

    const saObligationDate = formData.hmrc_next_obligation_due || formData.sa_due_date || null
    const saTaxCalculationAmount = formData.hmrc_tax_calculation_amount ?? formData.hmrc_tax_calculation ?? formData.hmrc_tax_due ?? formData.hmrc_latest_liability ?? null
    const saPenaltyAmount = formData.hmrc_latest_penalty ?? formData.hmrc_penalty_amount ?? formData.hmrc_total_penalties ?? formData.sa_penalties ?? null
    const saPenaltyDetails = formData.hmrc_penalty_description ?? formData.hmrc_penalty_reason ?? null
    const saPaymentCount = formData.hmrc_payment_count ?? formData.hmrc_total_payments ?? formData.sa_payment_count ?? null
    const saLastPaymentDate = formData.hmrc_latest_payment_date ?? formData.hmrc_last_payment_date ?? null

    const mtdBusinessList = toArray(formData.hmrc_mtd_businesses || formData.mtd_businesses)
    const mtdAccountingType =
        formData.hmrc_mtd_accounting_type ||
        formData.mtd_accounting_type ||
        mtdBusinessList?.[0]?.accountingType ||
        mtdBusinessList?.[0]?.accountingMethod ||
        null
    const mtdPeriodsOfAccountRaw =
        formData.hmrc_mtd_periods_of_account ||
        formData.mtd_periods_of_account ||
        mtdBusinessList?.[0]?.periodsOfAccount ||
        mtdBusinessList?.[0]?.accountingPeriod ||
        null
    const mtdPeriodsOfAccount = parseJsonMaybe(mtdPeriodsOfAccountRaw)
    const mtdPeriodsText =
        typeof mtdPeriodsOfAccount === 'string'
            ? mtdPeriodsOfAccount
            : Array.isArray(mtdPeriodsOfAccount)
                ? mtdPeriodsOfAccount
                    .map((p) => `${p?.startDate || p?.start || 'N/A'} → ${p?.endDate || p?.end || 'N/A'}`)
                    .join(' | ')
                : mtdPeriodsOfAccount && typeof mtdPeriodsOfAccount === 'object'
                    ? `${mtdPeriodsOfAccount.startDate || mtdPeriodsOfAccount.start || 'N/A'} → ${mtdPeriodsOfAccount.endDate || mtdPeriodsOfAccount.end || 'N/A'}`
                    : null
    const mtdLateAccountingDateRuleElection =
        formData.hmrc_mtd_late_accounting_date_rule_election ||
        formData.mtd_late_accounting_date_rule_election ||
        mtdBusinessList?.[0]?.lateAccountingDateRuleElection ||
        mtdBusinessList?.[0]?.lateAccountingDateElection ||
        null

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    };

    const copyText = async (valueToCopy, fallbackLabel) => {
        try {
            if (navigator?.clipboard?.writeText) {
                await navigator.clipboard.writeText(valueToCopy);
            } else {
                const textArea = document.createElement('textarea');
                textArea.value = valueToCopy;
                textArea.setAttribute('readonly', '');
                textArea.style.position = 'absolute';
                textArea.style.left = '-9999px';
                document.body.appendChild(textArea);
                textArea.select();
                document.execCommand('copy');
                document.body.removeChild(textArea);
            }

            return true;
        } catch {
            window.prompt(`Copy this ${fallbackLabel}:`, valueToCopy);
            return false;
        }
    };

    const handleLocalFolderLinkClick = async (event, link) => {
        event.preventDefault();

        const localPath = link?.localPath || String(link?.url || '').replace(/^ms-explorer:/i, '');
        const launchedViaHelper = await tryLaunchLocalFolderPath(localPath);

        if (launchedViaHelper) {
            setLauncherStatus('online');
            setLocalLaunchNotice('Opened local folder via StaffHub helper.');
            return;
        }

        setLauncherStatus('offline');

        try {
            window.location.href = link.url;
        } catch {
            // ignore launch failures; clipboard fallback still applies
        }

        await copyText(localPath || link.url, 'folder path');
        setLocalLaunchNotice('Attempted folder launch. Path copied — paste into File Explorer if blocked.');
    };

    const handleCopyResolvedLink = async (link) => {
        const isLocalLink = /^(file:\/\/\/|ms-explorer:)/i.test(String(link?.url || ''));
        const valueToCopy = isLocalLink
            ? (link?.localPath || String(link?.url || '').replace(/^ms-explorer:/i, ''))
            : String(link?.url || '');

        if (!valueToCopy) return;

        const copied = await copyText(valueToCopy, isLocalLink ? 'folder path' : 'link');
        if (copied) {
            setCopiedLocalLinkKey(String(link?.key || link?.label || 'copied'));
        }
    };

    const handleRestartLauncher = async () => {
        setIsRestartingLauncher(true);
        const restarted = await restartLocalFolderLauncher();

        if (!restarted) {
            await copyText("Start-ScheduledTask -TaskName 'StaffHub Folder Launcher'", 'launcher restart command');
            setLauncherStatus('offline');
            setLocalLaunchNotice('Could not restart automatically. Restart command copied for admin terminal.');
            setIsRestartingLauncher(false);
            return;
        }

        setLocalLaunchNotice('Launcher restarting — checking status…');
        const isOnline = await pollForLauncher(14000, 1500);
        setLauncherStatus(isOnline ? 'online' : 'offline');
        setLocalLaunchNotice(isOnline ? 'Launcher restarted successfully.' : 'Restart requested. Please retry in a moment.');
        setIsRestartingLauncher(false);
    };

    // Fields in readonlyFields are sourced from a linked table — display only, never written back
    const isReadonly = (field) => config?.readonlyFields?.includes(field);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSaving(true);
        await onSave(client.id, formData);
        setIsSaving(false);
        onClose();
    };

    const handleExpandAllSections = () => {
        setAccordionCommand((prev) => ({ open: true, nonce: prev.nonce + 1 }));
    };

    const handleCollapseAllSections = () => {
        setAccordionCommand((prev) => ({ open: false, nonce: prev.nonce + 1 }));
    };

    const handleDefaultSections = () => {
        setAccordionCommand((prev) => ({ ...prev, open: null }));
        setDefaultAccordionNonce((prev) => prev + 1);
    };

    const renderExternalLinkSection = (section) => {
        const referenceValue = getExternalReferenceValue(section);
        const editableReference = section.editableReference !== false && !isServiceModule;
        const resolvedSection = getAvailableExternalLinkSections({ ...effectiveSectionConfig, externalLinkSections: [section] }, linkContextData)[0];
        const visibleLinks = isServiceModule
            ? getServiceScopedLinks(config?.id, section.sectionLabel || section.providerLabel || '', resolvedSection?.links || [])
            : (resolvedSection?.links || []);
        const hasSectionLocalLinks = visibleLinks.some((link) => /^(file:\/\/\/|ms-explorer:)/i.test(String(link.url || '')));

        if (isServiceModule && visibleLinks.length === 0) return null;

        const sectionLabel = section.sectionLabel || section.providerLabel || '';
        return (
            <ModalAccordionSection
                key={sectionLabel || 'external-links'}
                title={sectionLabel || 'External Links'}
                icon={<LinkIcon className="w-4 h-4 text-sky-600" />}
                defaultOpen={isSectionDefaultOpen(sectionLabel)}
                defaultPresetOpen={false}
                forceOpenState={accordionCommand.open}
                forceSignal={accordionCommand.nonce}
                defaultSignal={defaultAccordionNonce}
            >
                <div className="space-y-3">
                    <div className="space-y-1">
                        <label className="text-xs font-medium text-slate-500 uppercase">
                            {section.referenceLabel || 'Reference'}
                        </label>
                        {editableReference ? (
                            <input
                                type="text"
                                name={section.referenceField}
                                value={formData[section.referenceField] || ''}
                                onChange={handleChange}
                                placeholder={section.referencePlaceholder || ''}
                                className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded text-sm focus:outline-none focus:ring-1 focus:ring-sky-500 text-slate-800 font-mono"
                            />
                        ) : (
                            <div className="px-3 py-2 bg-slate-100 border border-slate-200 rounded text-sm text-slate-700 font-mono">
                                {referenceValue || <span className="italic text-slate-400">N/A</span>}
                            </div>
                        )}
                        {section.referenceHelp && (
                            <p className="text-xs text-slate-500">{section.referenceHelp}</p>
                        )}
                    </div>

                    {referenceValue ? (
                        <div className="grid grid-cols-1 gap-2">
                            {localLaunchNotice && hasSectionLocalLinks && (
                                <div className="px-3 py-2 rounded-lg border border-amber-200 bg-amber-50 text-xs text-amber-800">
                                    {localLaunchNotice}
                                </div>
                            )}

                            {hasSectionLocalLinks && (
                                <div className={`px-3 py-2 rounded-lg border text-xs ${launcherStatus === 'online' ? 'border-emerald-200 bg-emerald-50 text-emerald-800' : launcherStatus === 'checking' ? 'border-slate-200 bg-slate-50 text-slate-700' : 'border-amber-200 bg-amber-50 text-amber-800'}`}>
                                    <div className="flex items-center justify-between gap-2">
                                        <span>
                                            {launcherStatus === 'online'
                                                ? 'Launcher Status: Connected (one-click local folder open enabled)'
                                                : launcherStatus === 'checking'
                                                    ? 'Launcher Status: Checking...'
                                                    : 'Launcher Status: Not running (click will copy folder path fallback).'}
                                        </span>
                                        <button
                                            type="button"
                                            onClick={handleRestartLauncher}
                                            disabled={isRestartingLauncher}
                                            className="inline-flex items-center gap-1 rounded-md border border-slate-300 bg-white/80 px-2 py-1 text-[10px] font-semibold text-slate-700 hover:bg-white disabled:opacity-50"
                                            title="Restart local launcher"
                                        >
                                            <RotateCcw className={`w-3 h-3 ${isRestartingLauncher ? 'animate-spin' : ''}`} />
                                            {isRestartingLauncher ? 'Restarting...' : 'Restart'}
                                        </button>
                                    </div>
                                </div>
                            )}

                            {visibleLinks.map((link) => {
                                const isLocalLink = /^(file:\/\/\/|ms-explorer:)/i.test(String(link.url || ''));
                                return (
                                    <div key={link.key} className="flex items-center gap-2">
                                        <a
                                            href={link.url}
                                            target={isLocalLink ? undefined : '_blank'}
                                            rel={isLocalLink ? undefined : 'noreferrer'}
                                            onClick={(event) => {
                                                if (isLocalLink) {
                                                    handleLocalFolderLinkClick(event, link);
                                                }
                                            }}
                                            className="flex-1 flex items-center justify-between gap-3 px-3 py-2 rounded-lg border border-sky-200 bg-sky-50 hover:bg-sky-100 text-sm text-sky-800 transition-colors"
                                        >
                                            <span className="font-medium">{link.label}</span>
                                        </a>
                                        <button
                                            type="button"
                                            onClick={() => handleCopyResolvedLink(link)}
                                            className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-2 py-2 text-[10px] font-semibold text-slate-600 hover:bg-slate-50 hover:text-slate-800 transition-colors flex-shrink-0"
                                            title={isLocalLink ? `Copy ${link.label} path` : `Copy ${link.label} link`}
                                        >
                                            {copiedLocalLinkKey === String(link?.key || link?.label || 'copied')
                                                ? <Check className="w-3 h-3 text-emerald-600" />
                                                : <Copy className="w-3 h-3" />}
                                            <span>
                                                {copiedLocalLinkKey === String(link?.key || link?.label || 'copied')
                                                    ? 'Copied'
                                                    : (isLocalLink ? 'Copy Path' : 'Copy Link')}
                                            </span>
                                        </button>
                                    </div>
                                );
                            })}
                            {visibleLinks.length === 0 && (
                                <div className="px-3 py-3 rounded-lg border border-dashed border-slate-300 bg-slate-50 text-xs text-slate-500">
                                    No links available for the current service engagement selection.
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="px-3 py-3 rounded-lg border border-dashed border-slate-300 bg-slate-50 text-xs text-slate-500">
                            {section.emptyState || 'Add a reference to enable shortcuts.'}
                        </div>
                    )}
                </div>
            </ModalAccordionSection>
        );
    };

    return createPortal(
        <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-in fade-in duration-200">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg border border-slate-200 overflow-hidden animate-in zoom-in-95 duration-200">
                <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-center bg-slate-50/50">
                    <div>
                        <h3 className="text-lg font-bold text-slate-800">{formData[mainField] || formData.name || `${entity.singular} Details`}</h3>
                        <p className="text-xs text-slate-500 font-mono mt-0.5">{entity.numberLabel}: {formData[numberField] || formData.number || 'N/A'}</p>
                        {isMtdItsa && (
                            <p className="text-xs text-slate-500 font-mono mt-0.5">MTD ITSA ID: {mtdIncomeTaxId || 'N/A'}</p>
                        )}
                        {config?.id === 'individual' && (
                            <>
                                <p className="text-xs text-slate-500 font-mono mt-0.5">NINO: {formData.individual_ni_number || 'N/A'}</p>
                                <p className="text-xs text-slate-500 font-mono mt-0.5">MTD ITSA ID: {mtdIncomeTaxId || 'N/A'}</p>
                            </>
                        )}
                    </div>
                    <button onClick={onClose} className="text-slate-400 hover:text-slate-600 hover:bg-slate-100 p-1 rounded-full"><XCircle className="w-6 h-6" /></button>
                </div>
                <form onSubmit={handleSubmit}>
                    <div className="p-6 space-y-3 max-h-[70vh] overflow-y-auto">
                        <div className="flex items-center justify-end gap-2">
                            <button
                                type="button"
                                onClick={handleDefaultSections}
                                className="px-3 py-1.5 text-xs font-semibold text-slate-700 bg-slate-100 border border-slate-200 rounded-lg hover:bg-slate-200"
                            >
                                Default
                            </button>
                            <button
                                type="button"
                                onClick={handleExpandAllSections}
                                className="px-3 py-1.5 text-xs font-semibold text-slate-700 bg-slate-100 border border-slate-200 rounded-lg hover:bg-slate-200"
                            >
                                Expand all
                            </button>
                            <button
                                type="button"
                                onClick={handleCollapseAllSections}
                                className="px-3 py-1.5 text-xs font-semibold text-slate-700 bg-slate-100 border border-slate-200 rounded-lg hover:bg-slate-200"
                            >
                                Collapse all
                            </button>
                        </div>

                        <ModalAccordionSection
                            title="Contact Details"
                            icon={<User className="w-4 h-4 text-blue-600" />}
                            defaultOpen
                            defaultPresetOpen
                            forceOpenState={accordionCommand.open}
                            forceSignal={accordionCommand.nonce}
                            defaultSignal={defaultAccordionNonce}
                        >
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-1">
                                    <label className="text-xs font-medium text-slate-500 uppercase">Contact Name</label>
                                    {isReadonly('pc_fname') ? (
                                        <div className="px-3 py-2 bg-slate-100 border border-slate-200 rounded text-sm text-slate-600">
                                            {formData.pc_fname || <span className="italic text-slate-400">N/A</span>}
                                            <span className="ml-2 text-[10px] text-slate-400">(managed on source page)</span>
                                        </div>
                                    ) : (
                                        <input type="text" name="pc_fname" value={formData.pc_fname || ''} onChange={handleChange} className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 text-slate-800"/>
                                    )}
                                </div>
                                <div className="space-y-1">
                                    <label className="text-xs font-medium text-slate-500 uppercase">Email</label>
                                    {isReadonly('pc_email') ? (
                                        <div className="px-3 py-2 bg-slate-100 border border-slate-200 rounded text-sm text-slate-600">
                                            {formData.pc_email || <span className="italic text-slate-400">N/A</span>}
                                            <span className="ml-2 text-[10px] text-slate-400">(managed on source page)</span>
                                        </div>
                                    ) : (
                                        <input type="email" name="pc_email" value={formData.pc_email || ''} onChange={handleChange} className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 text-slate-800"/>
                                    )}
                                </div>
                                <div className="space-y-1">
                                    <label className="text-xs font-medium text-slate-500 uppercase">Telephone</label>
                                    {isReadonly('pc_tel') ? (
                                        <div className="px-3 py-2 bg-slate-100 border border-slate-200 rounded text-sm text-slate-600">
                                            {formData.pc_tel || <span className="italic text-slate-400">N/A</span>}
                                            <span className="ml-2 text-[10px] text-slate-400">(managed on source page)</span>
                                        </div>
                                    ) : (
                                        <input type="text" name="pc_tel" value={formData.pc_tel || ''} onChange={handleChange} className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 text-slate-800"/>
                                    )}
                                </div>
                            </div>
                        </ModalAccordionSection>

                        <ModalAccordionSection
                            title="Notes"
                            icon={<FileText className="w-4 h-4 text-amber-500" />}
                            defaultPresetOpen
                            forceOpenState={accordionCommand.open}
                            forceSignal={accordionCommand.nonce}
                            defaultSignal={defaultAccordionNonce}
                        >
                            <textarea name="notes" value={formData.notes || ''} onChange={handleChange} rows={5} className="w-full px-4 py-3 bg-amber-50/50 border border-amber-200 rounded-lg text-sm text-slate-700 leading-relaxed resize-none focus:outline-none focus:ring-1 focus:ring-amber-400 focus:bg-white" placeholder="Client notes..."/>
                        </ModalAccordionSection>

                        {isServiceModule && (
                            <ModalAccordionSection
                                title={`${config?.label || 'Service'} Workflow`}
                                icon={<FileText className="w-4 h-4 text-slate-600" />}
                                defaultOpen
                                defaultPresetOpen={false}
                                forceOpenState={accordionCommand.open}
                                forceSignal={accordionCommand.nonce}
                                defaultSignal={defaultAccordionNonce}
                            >
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <div className="text-xs text-slate-500 uppercase font-semibold mb-1">Client Type</div>
                                        <div className="text-sm font-mono text-slate-800">{formatSourceTypeLabel(sourceTypeKey)}</div>
                                    </div>
                                    <div>
                                        <div className="text-xs text-slate-500 uppercase font-semibold mb-1">Service</div>
                                        <div className="text-sm font-mono text-slate-800">{displayValue(config?.label)}</div>
                                    </div>
                                    {workflowDueField && (
                                        <div>
                                            <div className="text-xs text-slate-500 uppercase font-semibold mb-1">Due Date</div>
                                            <div className="text-sm font-mono text-slate-800">{formatDateValue(formData[workflowDueField])}</div>
                                        </div>
                                    )}
                                    {workflowPlanField && (
                                        <div>
                                            <div className="text-xs text-slate-500 uppercase font-semibold mb-1">Plan Month</div>
                                            <div className="text-sm font-mono text-slate-800">{displayValue(formData[workflowPlanField])}</div>
                                        </div>
                                    )}
                                    {workflowStatusField && (
                                        <div>
                                            <div className="text-xs text-slate-500 uppercase font-semibold mb-1">Current Status</div>
                                            <div className="text-sm font-mono text-slate-800">{displayValue(formData[workflowStatusField])}</div>
                                        </div>
                                    )}
                                    {workflowRefField && (
                                        <div>
                                            <div className="text-xs text-slate-500 uppercase font-semibold mb-1">Reference</div>
                                            <div className="text-sm font-mono text-slate-800">{displayValue(formData[workflowRefField])}</div>
                                        </div>
                                    )}
                                    {workflowNextField && workflowNextField !== workflowRefField && (
                                        <div>
                                            <div className="text-xs text-slate-500 uppercase font-semibold mb-1">Next Milestone</div>
                                            <div className="text-sm font-mono text-slate-800">{displayValue(formData[workflowNextField])}</div>
                                        </div>
                                    )}
                                    {workflowStaffField && (
                                        <div>
                                            <div className="text-xs text-slate-500 uppercase font-semibold mb-1">Assigned Staff</div>
                                            <div className="text-sm font-mono text-slate-800">{displayValue(formData[workflowStaffField])}</div>
                                        </div>
                                    )}
                                </div>
                            </ModalAccordionSection>
                        )}

                        {config?.id === 'bookkeeping' && (
                            <ModalAccordionSection
                                title="Bookkeeping Details"
                                icon={<FileText className="w-4 h-4 text-teal-600" />}
                                defaultOpen
                                defaultPresetOpen={false}
                                forceOpenState={accordionCommand.open}
                                forceSignal={accordionCommand.nonce}
                                defaultSignal={defaultAccordionNonce}
                            >
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-1">
                                        <label className="text-xs font-medium text-slate-500 uppercase">Software</label>
                                        <input type="text" name="bookkeeping_software" value={formData.bookkeeping_software || ''} onChange={handleChange} placeholder="xero" className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded text-sm focus:outline-none focus:ring-1 focus:ring-teal-500 text-slate-800"/>
                                    </div>
                                    <div className="space-y-1">
                                        <label className="text-xs font-medium text-slate-500 uppercase">Frequency</label>
                                        <select name="bookkeeping_frequency" value={formData.bookkeeping_frequency || 'monthly'} onChange={handleChange} className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded text-sm focus:outline-none focus:ring-1 focus:ring-teal-500 text-slate-800">
                                            <option value="monthly">Monthly</option>
                                            <option value="quarterly">Quarterly</option>
                                            <option value="annual">Annual</option>
                                        </select>
                                    </div>
                                    <div className="space-y-1">
                                        <label className="text-xs font-medium text-slate-500 uppercase">VAT Registered</label>
                                        <select name="vat_registered" value={formData.vat_registered ? 'true' : 'false'} onChange={e => setFormData(prev => ({ ...prev, vat_registered: e.target.value === 'true' }))} className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded text-sm focus:outline-none focus:ring-1 focus:ring-teal-500 text-slate-800">
                                            <option value="false">No</option>
                                            <option value="true">Yes</option>
                                        </select>
                                    </div>
                                    {formData.vat_registered && (
                                        <div className="space-y-1">
                                            <label className="text-xs font-medium text-slate-500 uppercase">VAT Frequency</label>
                                            <select name="vat_frequency" value={formData.vat_frequency || 'quarterly'} onChange={handleChange} className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded text-sm focus:outline-none focus:ring-1 focus:ring-teal-500 text-slate-800">
                                                <option value="monthly">Monthly</option>
                                                <option value="quarterly">Quarterly</option>
                                            </select>
                                        </div>
                                    )}
                                    <div className="space-y-1 col-span-2">
                                        <label className="text-xs font-medium text-slate-500 uppercase">Period Reference</label>
                                        <input type="text" name="period_reference" value={formData.period_reference || ''} onChange={handleChange} placeholder="e.g. Apr 2026 or Q4 25/26" className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded text-sm focus:outline-none focus:ring-1 focus:ring-teal-500 text-slate-800"/>
                                    </div>
                                </div>
                            </ModalAccordionSection>
                        )}

                        {clientFolderSections.map(renderExternalLinkSection)}

                        {/* ── Engaged Services ── shown for core client pages (not service tabs) ── */}
                        {!isServiceModule && (() => {
                            const SERVICES = {
                                ch_company: [
                                    { key: 'svc_accounts_prep',           label: 'Accounts Preparation' },
                                    { key: 'svc_bookkeeping',             label: 'Bookkeeping' },
                                    { key: 'svc_ct600',                   label: 'CT600 Return' },
                                    { key: 'svc_confirmation_statement',  label: 'Confirmation Statement' },
                                    { key: 'svc_payroll',                 label: 'Payroll' },
                                    { key: 'svc_pension_ae',              label: 'Pension Auto-Enrolment' },
                                    { key: 'svc_vat_returns',             label: 'VAT Returns' },
                                    { key: 'svc_cfo',                     label: 'CFO' },
                                    { key: 'svc_mgmt_accounts',           label: 'Management Accounts' },
                                    { key: 'svc_cis',                     label: 'CIS' },
                                    { key: 'svc_registered_address',      label: 'Registered Address' },
                                    { key: 'svc_accounting_software',     label: 'Accounting Software' },
                                    { key: 'svc_consultation',            label: 'Consultation / Advice' },
                                ],
                                charity: [
                                    { key: 'svc_accounts_prep',           label: 'Accounts Preparation' },
                                    { key: 'svc_bookkeeping',             label: 'Bookkeeping' },
                                    { key: 'svc_charity_commission',      label: 'Charity Commission Return' },
                                    { key: 'svc_independent_exam',        label: 'Independent Examination' },
                                    { key: 'svc_payroll',                 label: 'Payroll' },
                                    { key: 'svc_pension_ae',              label: 'Pension Auto-Enrolment' },
                                    { key: 'svc_gift_aid',                label: 'Gift Aid' },
                                    { key: 'svc_accounting_software',     label: 'Accounting Software' },
                                    { key: 'svc_consultation',            label: 'Consultation / Advice' },
                                ],
                                individual: [
                                    { key: 'svc_self_assessment',         label: 'Self Assessment Tax Return' },
                                    { key: 'svc_mtd_itsa',                label: 'MTD ITSA' },
                                    { key: 'svc_cis',                     label: 'CIS' },
                                    { key: 'svc_bookkeeping',             label: 'Bookkeeping' },
                                    { key: 'svc_vat_returns',             label: 'VAT Returns' },
                                    { key: 'svc_payroll',                 label: 'Payroll' },
                                    { key: 'svc_pension_ae',              label: 'Pension Auto-Enrolment' },
                                    { key: 'svc_accounting_software',     label: 'Accounting Software' },
                                    { key: 'svc_consultation',            label: 'Consultation / Advice' },
                                ],
                                unregistered: [
                                    { key: 'svc_accounts_prep',           label: 'Accounts Preparation' },
                                    { key: 'svc_bookkeeping',             label: 'Bookkeeping' },
                                    { key: 'svc_independent_exam',        label: 'Independent Examination' },
                                    { key: 'svc_payroll',                 label: 'Payroll' },
                                    { key: 'svc_pension_ae',              label: 'Pension Auto-Enrolment' },
                                    { key: 'svc_vat_returns',             label: 'VAT Returns' },
                                    { key: 'svc_cfo',                     label: 'CFO' },
                                    { key: 'svc_mgmt_accounts',           label: 'Management Accounts' },
                                    { key: 'svc_cis',                     label: 'CIS' },
                                    { key: 'svc_gift_aid',                label: 'Gift Aid' },
                                    { key: 'svc_accounting_software',     label: 'Accounting Software' },
                                    { key: 'svc_consultation',            label: 'Consultation / Advice' },
                                ],
                                partnership: [
                                    { key: 'svc_accounts_prep',           label: 'Accounts Preparation' },
                                    { key: 'svc_bookkeeping',             label: 'Bookkeeping' },
                                    { key: 'svc_payroll',                 label: 'Payroll' },
                                    { key: 'svc_pension_ae',              label: 'Pension Auto-Enrolment' },
                                    { key: 'svc_vat_returns',             label: 'VAT Returns' },
                                    { key: 'svc_cfo',                     label: 'CFO' },
                                    { key: 'svc_mgmt_accounts',           label: 'Management Accounts' },
                                    { key: 'svc_cis',                     label: 'CIS' },
                                    { key: 'svc_registered_address',      label: 'Registered Address' },
                                    { key: 'svc_accounting_software',     label: 'Accounting Software' },
                                    { key: 'svc_consultation',            label: 'Consultation / Advice' },
                                ],
                            };
                            const services = SERVICES[effectiveSectionConfig?.id];
                            if (!services) return null;
                            return (
                                <ModalAccordionSection
                                    title="Engaged Services"
                                    icon={<ClipboardList className="w-4 h-4 text-violet-600" />}
                                    defaultOpen={svcModalConfig ? svcModalConfig.openEngagedServices : config?.id === 'ch_company'}
                                    defaultPresetOpen={false}
                                    forceOpenState={accordionCommand.open}
                                    forceSignal={accordionCommand.nonce}
                                    defaultSignal={defaultAccordionNonce}
                                >
                                    <div className="grid grid-cols-2 gap-2">
                                        {services.map(svc => {
                                            const active = Boolean(formData[svc.key]);
                                            return (
                                                <label
                                                    key={svc.key}
                                                    className={`flex items-center gap-2.5 px-3 py-2 rounded-lg border cursor-pointer text-xs font-medium transition-colors ${
                                                        active
                                                            ? 'bg-violet-50 border-violet-300 text-violet-800'
                                                            : 'bg-slate-50 border-slate-200 text-slate-500 hover:bg-slate-100'
                                                    }`}
                                                >
                                                    <input
                                                        type="checkbox"
                                                        name={svc.key}
                                                        checked={active}
                                                        onChange={handleChange}
                                                        className="sr-only"
                                                    />
                                                    <span className={`w-4 h-4 flex-shrink-0 rounded border-2 flex items-center justify-center transition-colors ${
                                                        active ? 'bg-violet-600 border-violet-600' : 'border-slate-300 bg-white'
                                                    }`}>
                                                        {active && (
                                                            <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 12 10">
                                                                <path d="M1 5l3.5 3.5L11 1" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                                            </svg>
                                                        )}
                                                    </span>
                                                    <span className="leading-tight">{svc.label}</span>
                                                </label>
                                            );
                                        })}
                                    </div>
                                    <p className="text-[11px] text-violet-500 italic">
                                        ✦ Enabling <strong>Bookkeeping</strong> automatically adds this client to the Bookkeeping workflow page.
                                    </p>
                                </ModalAccordionSection>
                            );
                        })()}

                        {remainingExternalLinkSections.map(renderExternalLinkSection)}

                        {isMtdItsa && (
                            <ModalAccordionSection
                                title="MTD ITSA Workflow Snapshot"
                                icon={<FileText className="w-4 h-4 text-orange-600" />}
                                defaultOpen
                                defaultPresetOpen={false}
                                forceOpenState={accordionCommand.open}
                                forceSignal={accordionCommand.nonce}
                                defaultSignal={defaultAccordionNonce}
                            >

                                <div className="bg-white border border-slate-200 rounded-lg p-3 space-y-3">
                                    <div className="grid grid-cols-2 gap-3">
                                        <div>
                                            <div className="text-xs text-slate-500 uppercase font-semibold mb-1">ITSA Scope</div>
                                            <div className="text-sm font-mono text-slate-800">{displayValue(formData.hmrc_itsa_status)}</div>
                                        </div>
                                        <div>
                                            <div className="text-xs text-slate-500 uppercase font-semibold mb-1">Agent Auth</div>
                                            <div className="text-sm font-mono text-slate-800">{displayValue(formData.hmrc_agent_auth_status)}</div>
                                        </div>
                                        <div>
                                            <div className="text-xs text-slate-500 uppercase font-semibold mb-1">Next Obligation</div>
                                            <div className="text-sm font-mono text-slate-800">{displayValue(formData.mtd_next_obligation_label)}</div>
                                        </div>
                                        <div>
                                            <div className="text-xs text-slate-500 uppercase font-semibold mb-1">Due (Next)</div>
                                            <div className="text-sm font-mono text-slate-800">{formatDateValue(formData.mtd_next_obligation_due)}</div>
                                        </div>
                                    </div>

                                    <div className="border-t border-slate-100 pt-3 grid grid-cols-2 sm:grid-cols-3 gap-3">
                                        <div>
                                            <div className="text-[11px] text-slate-500 uppercase font-semibold mb-1">Q1 Due</div>
                                            <div className="text-xs font-mono text-slate-800">{formatDateValue(formData.mtd_q1_due_date || formData.mtd_q1_due)}</div>
                                        </div>
                                        <div>
                                            <div className="text-[11px] text-slate-500 uppercase font-semibold mb-1">Q2 Due</div>
                                            <div className="text-xs font-mono text-slate-800">{formatDateValue(formData.mtd_q2_due_date || formData.mtd_q2_due)}</div>
                                        </div>
                                        <div>
                                            <div className="text-[11px] text-slate-500 uppercase font-semibold mb-1">Q3 Due</div>
                                            <div className="text-xs font-mono text-slate-800">{formatDateValue(formData.mtd_q3_due_date || formData.mtd_q3_due)}</div>
                                        </div>
                                        <div>
                                            <div className="text-[11px] text-slate-500 uppercase font-semibold mb-1">Q4 Due</div>
                                            <div className="text-xs font-mono text-slate-800">{formatDateValue(formData.mtd_q4_due_date || formData.mtd_q4_due)}</div>
                                        </div>
                                        <div>
                                            <div className="text-[11px] text-slate-500 uppercase font-semibold mb-1">Final Declaration Due</div>
                                            <div className="text-xs font-mono text-slate-800">{formatDateValue(formData.mtd_final_declaration_due)}</div>
                                        </div>
                                        <div>
                                            <div className="text-[11px] text-slate-500 uppercase font-semibold mb-1">Obligations Overdue</div>
                                            <div className="text-xs font-mono text-slate-800">{displayValue(formData.mtd_obligations_overdue ?? formData.mtd_overdue_obligations)}</div>
                                        </div>
                                    </div>

                                    <div className="border-t border-slate-100 pt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
                                        <div>
                                            <div className="text-[11px] text-slate-500 uppercase font-semibold mb-1">Quarter Statuses</div>
                                            <div className="text-xs text-slate-700 space-y-1">
                                                <div>Q1: <span className="font-mono">{displayValue(formData.mtd_status_q1)}</span></div>
                                                <div>Q2: <span className="font-mono">{displayValue(formData.mtd_status_q2)}</span></div>
                                                <div>Q3: <span className="font-mono">{displayValue(formData.mtd_status_q3)}</span></div>
                                                <div>Q4: <span className="font-mono">{displayValue(formData.mtd_status_q4)}</span></div>
                                                <div>Final: <span className="font-mono">{displayValue(formData.mtd_status_final)}</span></div>
                                            </div>
                                        </div>
                                        <div>
                                            <div className="text-[11px] text-slate-500 uppercase font-semibold mb-1">Planned Months</div>
                                            <div className="text-xs text-slate-700 space-y-1">
                                                <div>Q1: <span className="font-mono">{displayValue(formData.mtd_plan_q1)}</span></div>
                                                <div>Q2: <span className="font-mono">{displayValue(formData.mtd_plan_q2)}</span></div>
                                                <div>Q3: <span className="font-mono">{displayValue(formData.mtd_plan_q3)}</span></div>
                                                <div>Q4: <span className="font-mono">{displayValue(formData.mtd_plan_q4)}</span></div>
                                                <div>Final: <span className="font-mono">{displayValue(formData.mtd_plan_final)}</span></div>
                                            </div>
                                        </div>
                                    </div>

                                </div>
                            </ModalAccordionSection>
                        )}

                        {isMtdItsa && (
                            <ModalAccordionSection
                                title="MTD Business Details"
                                icon={<FileText className="w-4 h-4 text-indigo-600" />}
                                defaultPresetOpen={false}
                                forceOpenState={accordionCommand.open}
                                forceSignal={accordionCommand.nonce}
                                defaultSignal={defaultAccordionNonce}
                            >

                                <div className="bg-white border border-slate-200 rounded-lg p-3 space-y-3">
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                        <div>
                                            <div className="text-[11px] text-slate-500 uppercase font-semibold mb-1">Business Details</div>
                                            <div className="text-xs font-mono text-slate-800">
                                                {mtdBusinessList.length > 0 ? 'Retrieved' : 'N/A'}
                                            </div>
                                        </div>
                                        <div>
                                            <div className="text-[11px] text-slate-500 uppercase font-semibold mb-1">Accounting Type</div>
                                            <div className="text-xs font-mono text-slate-800">{displayValue(mtdAccountingType)}</div>
                                        </div>
                                        <div className="sm:col-span-2">
                                            <div className="text-[11px] text-slate-500 uppercase font-semibold mb-1">Periods of Account</div>
                                            <div className="text-xs font-mono text-slate-800 break-words">{displayValue(mtdPeriodsText)}</div>
                                        </div>
                                        <div className="sm:col-span-2">
                                            <div className="text-[11px] text-slate-500 uppercase font-semibold mb-1">Late Accounting Date Rule Election</div>
                                            <div className="text-xs font-mono text-slate-800">{displayValue(mtdLateAccountingDateRuleElection)}</div>
                                        </div>
                                    </div>

                                    <div>
                                        <div className="text-[11px] text-slate-500 uppercase font-semibold mb-1">List Of Businesses</div>
                                        {mtdBusinessList.length > 0 ? (
                                            <div className="space-y-2">
                                                {mtdBusinessList.map((business, idx) => (
                                                    <div key={`${business?.businessId || business?.id || idx}-${idx}`} className="text-xs text-slate-700 border border-slate-200 rounded p-2 bg-slate-50">
                                                        <div><span className="font-semibold">Business:</span> <span className="font-mono">{displayValue(business?.tradingName || business?.businessName || business?.name || business?.businessId || business?.id)}</span></div>
                                                        <div><span className="font-semibold">Business ID:</span> <span className="font-mono">{displayValue(business?.businessId || business?.id)}</span></div>
                                                        <div><span className="font-semibold">Type:</span> <span className="font-mono">{displayValue(business?.type || business?.incomeSourceType || business?.businessType)}</span></div>
                                                    </div>
                                                ))}
                                            </div>
                                        ) : (
                                            <div className="text-xs font-mono text-slate-800">N/A</div>
                                        )}
                                    </div>
                                </div>
                            </ModalAccordionSection>
                        )}

                        {isIndividual && (
                            <ModalAccordionSection
                                title="Self Assessment Balance & Transactions"
                                icon={<FileText className="w-4 h-4 text-emerald-600" />}
                                defaultPresetOpen={false}
                                forceOpenState={accordionCommand.open}
                                forceSignal={accordionCommand.nonce}
                                defaultSignal={defaultAccordionNonce}
                            >
                                <div className="bg-white border border-slate-200 rounded-lg p-3 space-y-3">
                                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
                                        <div>
                                            <div className="text-xs text-slate-500 uppercase font-semibold mb-1">SA Obligations</div>
                                            <div className="text-sm font-mono text-slate-800">
                                                Due: {formatDateValue(saObligationDate)}
                                            </div>
                                            <div className="text-xs text-slate-500 uppercase font-semibold mt-2">Tax Year</div>
                                            <div className="text-sm font-mono text-slate-800">
                                                {formData.hmrc_next_tax_year || formData.sa_tax_year_end || 'N/A'}
                                            </div>
                                        </div>
                                        <div>
                                            <div className="text-xs text-slate-500 uppercase font-semibold mb-1">SA Tax Calculation</div>
                                            <div className="text-sm font-mono text-slate-800">
                                                {saTaxCalculationAmount !== null && saTaxCalculationAmount !== undefined
                                                    ? formatCurrency(saTaxCalculationAmount)
                                                    : 'N/A'}
                                            </div>
                                            <div className="text-xs text-slate-500 uppercase font-semibold mt-2">Last Filed</div>
                                            <div className="text-sm font-mono text-slate-800">
                                                {formData.hmrc_last_tax_year_filed || 'N/A'}
                                            </div>
                                        </div>
                                        <div>
                                            <div className="text-xs text-slate-500 uppercase font-semibold mb-1">SA Penalties</div>
                                            <div className="text-sm font-mono text-slate-800">
                                                {saPenaltyAmount !== null && saPenaltyAmount !== undefined
                                                    ? formatCurrency(saPenaltyAmount)
                                                    : 'N/A'}
                                            </div>
                                            {saPenaltyDetails && (
                                                <div className="text-xs text-slate-500 mt-2">{saPenaltyDetails}</div>
                                            )}
                                        </div>
                                        <div>
                                            <div className="text-xs text-slate-500 uppercase font-semibold mb-1">SA Payments</div>
                                            <div className="text-sm font-mono text-slate-800">
                                                {formData.hmrc_latest_payment !== null && formData.hmrc_latest_payment !== undefined
                                                    ? formatCurrency(formData.hmrc_latest_payment)
                                                    : 'N/A'}
                                            </div>
                                            <div className="text-xs text-slate-500 uppercase font-semibold mt-2">Last Payment</div>
                                            <div className="text-sm font-mono text-slate-800">
                                                {formatDateValue(saLastPaymentDate)}
                                            </div>
                                            {saPaymentCount !== null && saPaymentCount !== undefined && (
                                                <div className="text-xs text-slate-500 mt-2">Payments: {saPaymentCount}</div>
                                            )}
                                        </div>
                                    </div>
                                    <div className="border-t border-slate-100 pt-3 space-y-2">
                                        <div className="text-xs text-slate-500 uppercase font-semibold mb-1">Liabilities Balance Outstanding</div>
                                        <div className="text-lg font-bold font-mono text-slate-800 bg-red-50 border border-red-200 rounded p-2">
                                            {formData.hmrc_latest_liability !== null && formData.hmrc_latest_liability !== undefined
                                                ? formatCurrency(formData.hmrc_latest_liability)
                                                : 'N/A'}
                                        </div>
                                    </div>
                                    <div className="border-t border-slate-100 pt-3">
                                        <div className="text-xs text-slate-500 uppercase font-semibold mb-1">ITSA Status</div>
                                        <div className="text-sm font-mono text-slate-800">
                                            {formData.hmrc_itsa_status ? formData.hmrc_itsa_status : 'N/A'}
                                        </div>
                                    </div>
                                    {formData.hmrc_last_sync_at && (
                                        <div className="text-[11px] text-slate-500 italic pt-2 border-t border-slate-100">
                                            Last synced: {new Date(formData.hmrc_last_sync_at).toLocaleString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                        </div>
                                    )}
                                </div>
                            </ModalAccordionSection>
                        )}

                        {isIndividual && (
                            <ModalAccordionSection
                                title="HMRC Connection"
                                icon={<LinkIcon className="w-4 h-4 text-emerald-600" />}
                                defaultPresetOpen={false}
                                forceOpenState={accordionCommand.open}
                                forceSignal={accordionCommand.nonce}
                                defaultSignal={defaultAccordionNonce}
                            >
                                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 space-y-3">
                                    <div>
                                        <div className="text-xs text-slate-500 uppercase font-semibold mb-1">Status</div>
                                        <span className={`inline-flex items-center px-2 py-0.5 rounded border text-xs font-semibold ${hmrcAgentStatusColorMap[effectiveHmrcAgentStatus] || hmrcAgentStatusColorMap.unknown}`}>
                                            {hmrcAgentStatusLabelMap[effectiveHmrcAgentStatus] || hmrcAgentStatusLabelMap.unknown}
                                        </span>
                                        <div className="text-[11px] text-slate-500 mt-1">Token: {hmrcStatusLabelMap[hmrcAuthStatus] || hmrcStatusLabelMap.not_connected}</div>
                                        {formData.hmrc_last_sync_at && (
                                            <div className="text-[11px] text-slate-500 mt-2">
                                                Last Sync: {new Date(formData.hmrc_last_sync_at).toLocaleString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                            </div>
                                        )}
                                    </div>
                                    <div className="flex flex-wrap items-center gap-2 pt-1">
                                        <button
                                            type="button"
                                            onClick={() => onConnectHmrc?.(client.id, 'agent')}
                                            disabled={isConnectingHmrc}
                                            className="px-3 py-2 text-xs font-bold text-white bg-emerald-600 hover:bg-emerald-700 disabled:bg-emerald-400 rounded-lg"
                                        >
                                            {isConnectingHmrc ? 'Starting...' : 'Connect via Agent'}
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => onConnectHmrc?.(client.id, 'client')}
                                            disabled={isConnectingHmrc}
                                            className="px-3 py-2 text-xs font-bold text-white bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 rounded-lg"
                                        >
                                            {isConnectingHmrc ? 'Starting...' : 'Connect via Client'}
                                        </button>
                                    </div>
                                </div>

                                {/* ── Agent Authorisation ── */}
                                <div className="bg-white border border-slate-200 rounded-lg p-3 space-y-2.5">
                                    <div className="text-xs text-slate-500 uppercase font-semibold tracking-wide">Agent Authorisation (MTD-IT)</div>

                                    {/* Status badge */}
                                    {hmrcInviteStatus ? (
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <span className={`inline-flex items-center px-2 py-0.5 rounded border text-xs font-semibold ${hmrcInviteStatusColorMap[hmrcInviteStatus] || 'bg-slate-100 text-slate-600 border-slate-200'}`}>
                                                {hmrcInviteStatusLabelMap[hmrcInviteStatus] || hmrcInviteStatus}
                                            </span>
                                            {hmrcInviteId && (
                                                <span className="text-[10px] text-slate-400 font-mono truncate max-w-[160px]" title={hmrcInviteId}>
                                                    ID: {hmrcInviteId}
                                                </span>
                                            )}
                                        </div>
                                    ) : (
                                        <div className="text-xs text-slate-500 italic">No authorisation requested yet.</div>
                                    )}

                                    {/* Postcode input — shown when a new request can be sent */}
                                    {(canRequestNew || isInvitePending) && (
                                        <div className="space-y-1">
                                            <label className="text-[11px] font-medium text-slate-500 uppercase">
                                                Client's Postcode (known fact)
                                            </label>
                                            <input
                                                type="text"
                                                value={postcodeInput}
                                                onChange={e => setPostcodeInput(e.target.value.toUpperCase())}
                                                placeholder="e.g. SW1A 1AA"
                                                maxLength={8}
                                                className="w-full px-3 py-1.5 bg-slate-50 border border-slate-200 rounded text-xs focus:outline-none focus:ring-1 focus:ring-emerald-500 text-slate-800 font-mono"
                                            />
                                        </div>
                                    )}

                                    {/* Action buttons */}
                                    <div className="flex flex-wrap items-center gap-2 pt-0.5">
                                        {canRequestNew && (
                                            <button
                                                type="button"
                                                onClick={() => onRequestAuth?.(client.id, postcodeInput)}
                                                disabled={isRequestingAuth || !postcodeInput.trim()}
                                                className="px-3 py-2 text-xs font-bold text-white bg-purple-600 hover:bg-purple-700 disabled:bg-purple-300 rounded-lg"
                                            >
                                                {isRequestingAuth ? 'Requesting...' : (hmrcInviteStatus ? 'Request New Authorisation' : 'Request Authorisation')}
                                            </button>
                                        )}
                                        {(isInvitePending || isInviteAccepted || hmrcInviteStatus === 'accepted_simulated' || hmrcInviteStatus === 'accepted_conflict') && (
                                            <button
                                                type="button"
                                                onClick={() => onCheckInviteStatus?.(client.id)}
                                                disabled={isCheckingInvite}
                                                className="px-3 py-2 text-xs font-bold text-slate-700 bg-slate-100 hover:bg-slate-200 disabled:bg-slate-50 border border-slate-300 rounded-lg"
                                            >
                                                {isCheckingInvite ? 'Checking...' : 'Check Status'}
                                            </button>
                                        )}
                                        {isInvitePending && (
                                            <button
                                                type="button"
                                                onClick={() => onOpenInviteLink?.(client.id)}
                                                disabled={isCheckingInvite || isRequestingAuth}
                                                className="px-3 py-2 text-xs font-bold text-white bg-emerald-600 hover:bg-emerald-700 disabled:bg-emerald-300 rounded-lg"
                                            >
                                                Open Client Link
                                            </button>
                                        )}
                                        {isInvitePending && hmrcEnvironment === 'sandbox' && (
                                            <button
                                                type="button"
                                                onClick={() => onSandboxAccept?.(client.id)}
                                                disabled={isCheckingInvite || isRequestingAuth}
                                                className="px-3 py-2 text-xs font-bold text-white bg-amber-500 hover:bg-amber-600 disabled:bg-amber-300 rounded-lg"
                                            >
                                                ⚡ Accept (Sandbox)
                                            </button>
                                        )}
                                    </div>

                                    {isInviteAccepted && (
                                        <div className="text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-2 py-1">
                                            ✓ Agent is authorised. Run Sync HMRC Data to fetch this client's data.
                                        </div>
                                    )}
                                    {showClientOrAgentNotAuthorisedWarning && (
                                        <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                                            ⚠ HMRC currently reports this agent is not authorised for this client. Use Check Status to refresh invitation state.
                                        </div>
                                    )}
                                    {hmrcInviteStatus === 'accepted_simulated' && (
                                        <div className="text-[11px] text-blue-700 bg-blue-50 border border-blue-200 rounded px-2 py-1">
                                            ⚠ Sandbox-only simulated acceptance. Real HMRC authorisation may still be missing for this client.
                                        </div>
                                    )}
                                </div>
                                {formData.hmrc_last_error && (
                                    <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded p-2">
                                        {formData.hmrc_last_error}
                                    </div>
                                )}
                            </ModalAccordionSection>
                        )}
                    </div>
                    <div className="px-6 py-4 bg-slate-50 border-t border-slate-200 flex justify-end gap-3">
                        <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-800 hover:bg-slate-200 rounded-lg">Cancel</button>
                        <button type="submit" disabled={isSaving} className="flex items-center gap-2 px-6 py-2 text-sm font-bold text-white bg-blue-600 hover:bg-blue-700 rounded-lg shadow-sm">{isSaving ? 'Saving...' : <><Save className="w-4 h-4" /> Save</>}</button>
                    </div>
                </form>
            </div>
        </div>,
        document.body
    );
};

export default ClientDetailModal;